/**
 * /pvp — public PVP board.
 *
 * Kills come from the bot's PalDefender death-line pipeline (`pvp_stats`).
 * That table only holds running totals, so "this week" is a delta against
 * the backend's daily snapshots — see api/routes/pvp.py.
 */
import { useEffect, useMemo, useState } from "react";
import { Crown, Skull, Swords, Target } from "lucide-react";
import { PageHeader, Loader } from "@/components/Atoms";
import { api, apiError } from "@/lib/api";

const MEDALS = ["text-[#e8c56a]", "text-[#cfd3d8]", "text-[#c08457]"];

export default function Pvp() {
  const [board, setBoard] = useState(null);
  const [window_, setWindow] = useState("all");
  const [serverIdx, setServerIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/pvp/leaderboard?window=${window_}`)
      .then((r) => {
        if (!alive) return;
        setBoard(r.data);
        setError("");
      })
      .catch((e) => alive && setError(apiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [window_]);

  const servers = board?.servers || [];
  const current = useMemo(
    () => servers[Math.min(serverIdx, Math.max(servers.length - 1, 0))],
    [servers, serverIdx],
  );

  const bestKd = useMemo(() => {
    const rows = current?.rows || [];
    return rows.reduce((best, r) => (!best || r.kd > best.kd ? r : best), null);
  }, [current]);

  return (
    <div className="space-y-6" data-testid="pvp-page">
      <PageHeader
        title="PVP Board"
        subtitle="Every player kill the servers log, ranked. Hunt accordingly."
      />

      <div className="glass-card p-5">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <Swords size={16} className="text-[var(--error)]" />
          <h3 className="text-lg">Top killers</h3>
          <div className="ml-auto flex gap-2">
            {[
              ["week", "This week"],
              ["all", "All time"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setWindow(k)}
                className={`btn text-xs ${window_ === k ? "" : "btn-secondary"}`}
                data-testid={`pvp-window-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Loader label="Counting bodies…" />
        ) : error ? (
          <p className="text-sm text-[var(--error)]" data-testid="pvp-error">
            {error}
          </p>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-4">
              {servers.map((s, i) => (
                <button
                  key={s.server}
                  onClick={() => setServerIdx(i)}
                  className={`btn text-xs ${i === serverIdx ? "" : "btn-secondary"}`}
                  data-testid={`pvp-server-tab-${i}`}
                >
                  {s.server}
                  <span className="opacity-60 ml-1.5">{s.total_kills}</span>
                </button>
              ))}
            </div>

            {bestKd && (
              <div
                className="flex items-center gap-3 mb-4 rounded-xl px-4 py-3"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                data-testid="pvp-best-kd"
              >
                <Target size={15} className="text-[var(--teal)]" />
                <span className="text-xs uppercase tracking-wider text-[var(--muted)]">
                  Best K/D
                </span>
                <span className="text-sm" style={bestKd.color ? { color: bestKd.color } : undefined}>
                  {bestKd.player}
                </span>
                <span className="ml-auto text-sm glow-gold">{bestKd.kd}</span>
              </div>
            )}

            {!current || current.rows.length === 0 ? (
              <p
                className="text-sm text-[var(--muted)] py-6 text-center"
                data-testid="pvp-empty"
              >
                No kills recorded {window_ === "week" ? "this week" : "yet"} on{" "}
                {current?.server || "this server"}.
              </p>
            ) : (
              <div className="divide-y divide-white/5" data-testid="pvp-rows">
                {current.rows.map((row) => (
                  <div
                    key={`${current.server}-${row.player}`}
                    className="py-3 flex items-center gap-3"
                    data-testid={`pvp-row-${row.rank}`}
                  >
                    <span
                      className={`w-7 text-center font-semibold ${
                        MEDALS[row.rank - 1] || "text-[var(--muted)]"
                      }`}
                    >
                      {row.rank <= 3 ? <Crown size={14} className="inline" /> : row.rank}
                    </span>
                    <PlayerFace row={row} />
                    <span
                      className="flex-1 min-w-0 truncate text-sm"
                      style={row.color ? { color: row.color } : undefined}
                    >
                      {row.player}
                    </span>
                    <span className="text-xs text-[var(--muted)] hidden sm:inline">
                      {row.deaths} deaths
                    </span>
                    <span className="text-xs text-[var(--muted)] w-16 text-right hidden sm:inline">
                      {row.kd} K/D
                    </span>
                    <span className="text-sm glow-gold w-12 text-right">{row.kills}</span>
                  </div>
                ))}
              </div>
            )}

            <p className="text-[11px] text-[var(--muted)] mt-4">
              {window_ === "week"
                ? board?.snapshot_since
                  ? `Weekly board resets Monday 00:00 UTC (measured from ${board.snapshot_since}).`
                  : "Weekly tracking just started — totals fill in from the next snapshot."
                : "All-time totals since PVP tracking went live."}{" "}
              Link your Discord with <code>/link</code> to show your picture and role colour here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function PlayerFace({ row }) {
  const [broken, setBroken] = useState(false);
  if (row.avatar_url && !broken) {
    return (
      <img
        src={row.avatar_url}
        alt=""
        className="w-7 h-7 rounded-full object-cover border border-white/15 shrink-0"
        loading="lazy"
        onError={() => setBroken(true)}
        data-testid={`pvp-avatar-${row.rank}`}
      />
    );
  }
  return (
    <span
      className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center border border-white/10 text-[10px] text-[var(--muted)]"
      style={{ background: "rgba(255,255,255,0.04)" }}
    >
      <Skull size={12} />
    </span>
  );
}
